import { useState } from "react";
import { useTimelineStore } from "../../features/timeline";

// Temporary notice for the reload-tree / "(empty message)" bug fixed in
// extension 0.1.6. The bug is Claude-only (ChatGPT's frontend does fire the
// conversation fetch on reload, so its interceptor path still works), so we
// gate on the active platform. `extensionVersion` is *not* used as a gate
// because that field was added in the same release as the fix — no shipped
// version reports it, so it can't distinguish broken from fixed users. Once
// the fixed extension has propagated through the Chrome Web Store review and
// auto-update window (give it ~1–2 weeks), delete this component and its
// mount point in App.tsx.
const DISMISS_STORAGE_KEY = "claudafinil_banner_dismissed_reload-tree-fix-0.1.6";

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, "1");
  } catch {
    // localStorage unavailable (private mode, etc.) — accept the cost of the
    // banner reappearing on next load.
  }
}

export function KnownIssueBanner(): JSX.Element | null {
  const activePlatform = useTimelineStore((state) => state.activePlatform);
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());

  // Wait for the handshake to report a platform before deciding — showing the
  // banner pre-handshake and then yanking it would flash. The bug is
  // Claude-only, so only show it once we know the active platform is claude.
  if (activePlatform !== "claude") return null;
  if (dismissed) return null;

  return (
    <div
      role="status"
      className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      <div className="flex-1 leading-relaxed">
        <strong className="font-semibold">Known issue:</strong> the conversation
        tree may show empty or fail to load after a page reload. A fix has been
        submitted to the Chrome Web Store and should reach you within a few
        days. In the meantime, clicking a chat from the Claude.ai sidebar will
        load the tree correctly.
      </div>
      <button
        type="button"
        onClick={() => {
          writeDismissed();
          setDismissed(true);
        }}
        className="shrink-0 rounded px-1 text-amber-900/70 hover:bg-amber-100 hover:text-amber-900"
        aria-label="Dismiss known-issue notice"
      >
        ✕
      </button>
    </div>
  );
}
